import React, { forwardRef, useId, useState } from "react";

/**
 * Enhanced form input component with comprehensive styling, accessibility, and UX features.
 *
 * Features:
 * - Clear, accessible error handling
 * - Optional left/right icons
 * - Password visibility toggle
 * - Focus and hover states
 * - Character counter for textarea/text inputs
 * - Comprehensive input validation
 * - Label animations
 * - Dark mode support
 *
 * @param {Object} props
 * @param {string} props.id - Input ID (generated if not provided)
 * @param {string} props.name - Input name attribute
 * @param {string} props.label - Label text
 * @param {string} props.type - Input type (text, email, password, etc.)
 * @param {string} props.value - Input value
 * @param {Function} props.onChange - Change handler
 * @param {boolean} props.required - Whether the input is required
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.error - Error message
 * @param {string} props.hint - Helper text
 * @param {number} props.maxLength - Maximum characters
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {React.ReactNode} props.icon - Icon to display
 * @param {string} props.iconPosition - Position of icon ('left' or 'right')
 * @param {Function} props.onFocus - Focus handler
 * @param {Function} props.onBlur - Blur handler
 * @param {string} props.className - Additional class names (wrapper)
 * @param {string} props.inputClassName - Additional class names on the <input>
 * @param {string} props.size - Input size (sm, md, lg)
 * @param {Object} props.validation - Validation rules
 * @param {boolean} props.autoFocus - Whether to auto-focus the input
 */
const FormInput = forwardRef(
  (
    {
      id,
      name,
      label,
      type = "text",
      value,
      onChange,
      required = false,
      placeholder = "",
      error = "",
      hint,
      maxLength,
      disabled = false,
      icon,
      iconPosition = "right",
      onFocus,
      onBlur,
      className = "",
      inputClassName = "",
      size = "md",
      validation,
      autoFocus = false,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [localValue, setLocalValue] = useState(value || "");

    // Generate a stable ID for label, hint, and error associations.
    const generatedInputId = useId();
    const inputId = id ?? `input-${name || "field"}-${generatedInputId}`;

    // Handle input change
    const handleChange = (e) => {
      setLocalValue(e.target.value);
      if (onChange) onChange(e);
    };

    // Handle focus events
    const handleFocus = (e) => {
      setIsFocused(true);
      if (onFocus) onFocus(e);
    };

    // Handle blur events
    const handleBlur = (e) => {
      setIsFocused(false);
      if (onBlur) onBlur(e);
    };

    // Toggle password visibility
    const togglePasswordVisibility = () => {
      setShowPassword(!showPassword);
    };

    // Size classes for input and icon
    const sizeClasses = {
      sm: "h-7 text-xs px-2",
      md: "",
      lg: "h-9",
    };

    const iconSizeClasses = {
      sm: "h-3.5 w-3.5",
      md: "h-4 w-4",
      lg: "h-5 w-5",
    };

    // Calculate padding based on icon position
    const iconPaddingClasses = icon
      ? iconPosition === "left"
        ? size === "sm"
          ? "pl-7"
          : size === "md"
            ? "pl-9"
            : "pl-11"
        : size === "sm"
          ? "pr-7"
          : size === "md"
            ? "pr-9"
            : "pr-11"
      : "";

    // Special handling for password eye icon
    const hasPasswordToggle = type === "password";
    const passwordPaddingClass = hasPasswordToggle
      ? size === "sm"
        ? "pr-7"
        : size === "md"
          ? "pr-9"
          : "pr-11"
      : "";

    // Handle the input type for password visibility
    const inputType = type === "password" && showPassword ? "text" : type;

    // Character counter
    const renderCharCounter = () => {
      if (!maxLength) return null;

      const count = localValue?.length || 0;
      const isNearLimit = count > maxLength * 0.8;
      const isAtLimit = count >= maxLength;

      const counterClass = isAtLimit
        ? "text-sg-danger"
        : isNearLimit
          ? "text-sg-warn"
          : "text-sg-ink-3";

      return (
        <div className={`font-mono text-[11px] ${counterClass} mt-1 text-right`}>
          {count}/{maxLength}
        </div>
      );
    };

    // Password toggle eye icon
    const renderPasswordToggle = () => (
      <button
        type="button"
        className="absolute inset-y-0 right-0 min-h-[44px] min-w-[44px] pr-3 flex items-center justify-end text-sg-ink-3 hover:text-sg-ink transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--sg-focus)] rounded-md"
        onClick={togglePasswordVisibility}
        aria-label={showPassword ? "Hide password" : "Show password"}
        aria-controls={inputId}
      >
        {showPassword ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={iconSizeClasses[size]}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
            />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={iconSizeClasses[size]}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
        )}
      </button>
    );

    return (
      <div className={`relative ${className}`}>
        {/* Label with animated float effect */}
        {label && (
          <label
            htmlFor={inputId}
            className={`sg-field-label ${
              error ? "!text-sg-danger" : isFocused ? "!text-sg-ink" : ""
            }`}
          >
            <div className="flex items-center">
              {label}
              {required && (
                <span className="text-sg-danger ml-1" aria-hidden="true">
                  *
                </span>
              )}
            </div>
          </label>
        )}

        {/* Input container */}
        <div className="relative group">
          {/* Left icon with animated color transition */}
          {icon && iconPosition === "left" && (
            <div
              className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${
                isFocused ? "text-sg-ink" : "text-sg-ink-3"
              } transition-colors duration-150`}
            >
              {icon}
            </div>
          )}

          {/* Input element with enhanced animations */}
          <input
            ref={ref}
            id={inputId}
            name={name}
            type={inputType}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            maxLength={maxLength}
            autoFocus={autoFocus}
            onFocus={handleFocus}
            onBlur={handleBlur}
            aria-invalid={error ? "true" : "false"}
            aria-describedby={
              error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined
            }
            className={`
            sg-field
            ${sizeClasses[size]}
            ${iconPaddingClasses}
            ${passwordPaddingClass}
            ${inputClassName}
          `}
            {...props}
          />

          {/* Right icon with animated color transition */}
          {icon && iconPosition === "right" && (
            <div
              className={`absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none ${
                isFocused ? "text-sg-ink" : "text-sg-ink-3"
              } transition-colors duration-150`}
            >
              {icon}
            </div>
          )}

          {/* Enhanced password visibility toggle with animation */}
          {hasPasswordToggle && renderPasswordToggle()}
        </div>

        {/* Error message with enhanced animation */}
        {error && (
          <div className="overflow-hidden">
            <p
              className="sg-field-msg"
              data-tone="error"
              id={`${inputId}-error`}
              role="alert"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span>{error}</span>
            </p>
          </div>
        )}

        {/* Helper text with icon */}
        {hint && !error && (
          <div className="overflow-hidden">
            <p
              className="sg-field-msg"
              id={`${inputId}-hint`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5 mt-px flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{hint}</span>
            </p>
          </div>
        )}

        {/* Enhanced character counter with animation */}
        {maxLength && (
          <div className="flex justify-end mt-1.5 overflow-hidden">
            {renderCharCounter()}
          </div>
        )}
      </div>
    );
  },
);

FormInput.displayName = "FormInput";

export default FormInput;
